import { useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Cable, Plus, ShieldOff } from 'lucide-react';
import { useTradingMarketQuotes, useTradingOverview } from '@/api/queries';
import { tradingApi, type LiveQuoteSnapshot, type TradingDeploymentDto, type TradingEnvironment, type TradingGatewayDto } from '@/api/trading';
import { Alert } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button, ButtonLink } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Select } from '@/components/ui/Select';
import { Table } from '@/components/ui/Table';
import { EnvironmentBadge, LifecycleBadge, Metric, formatMoney, formatPrice, formatTime } from '@/components/trading/TradingShared';
import { useOptionalAuth } from '@/contexts/AuthContext';
import styles from './TradingPage.module.css';
import { DeploymentWizard } from '@/components/trading/DeploymentWizard';
import { CREATE_SOURCE_PARAMS, routes, withoutParams } from '@/lib/routePaths';

const activeStatuses = ['Starting', 'Reconciling', 'WarmingUp', 'Active', 'Offline', 'RiskPaused', 'StopPending'];

export default function TradingPage() {
  const user = useOptionalAuth()?.user;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [showArchived, setShowArchived] = useState(false);
  const overview = useTradingOverview(showArchived);
  const [environment, setEnvironment] = useState('');
  const [status, setStatus] = useState('attention');
  const [account, setAccount] = useState('');
  const [marketAccount, setMarketAccount] = useState('');
  const [strategy, setStrategy] = useState('');
  const [stopAll, setStopAll] = useState(false);
  const streamingAccounts = useMemo(
    () => (overview.data?.accounts ?? []).filter((item) => item.connected && item.supportsLiveQuotes),
    [overview.data],
  );
  const activeMarketAccount = streamingAccounts.some((item) => item.id === marketAccount) ? marketAccount : '';
  const marketQuotes = useTradingMarketQuotes(streamingAccounts.length > 0);
  const marketRows = useMemo(
    () => buildMarketRows(streamingAccounts, marketQuotes.data ?? [], activeMarketAccount),
    [activeMarketAccount, marketQuotes.data, streamingAccounts],
  );

  const rows = useMemo(() => (overview.data?.deployments ?? []).filter((item) => {
    const attention = !!item.error || ['Offline', 'Faulted', 'RiskPaused', 'RecoveryRequired', 'StoppedWithExposure', 'ActivationFailed', 'ForceStopped'].includes(item.status);
    return (!environment || environmentOf(item) === environment) &&
      (!account || item.gatewayId === account) && (!strategy || item.strategySnapshotHash === strategy) &&
      (!status || (status === 'attention' ? attention || activeStatuses.includes(item.status) : item.status === status));
  }), [overview.data, environment, account, strategy, status]);
  const active = (overview.data?.deployments ?? []).filter((item) => activeStatuses.includes(item.status));
  const error = overview.error ?? marketQuotes.error;

  function openCreate() {
    setSearchParams((current) => {
      const next = new URLSearchParams(current);
      next.set('create', '1');
      next.delete('backtestId');
      return next;
    });
  }

  function closeCreate() {
    setSearchParams((current) => withoutParams(current, CREATE_SOURCE_PARAMS), { replace: true });
  }

  return <div className={styles.page}>
    <div className={styles.heading}>
      <div><h1>Trading operations</h1><p>One operational view for Paper, Demo, and safety-gated Live deployments.</p></div>
      <div className={styles.headingActions}>
        <label><input type="checkbox" checked={showArchived} onChange={event=>setShowArchived(event.target.checked)}/> Show archived</label>
        {(user?.role === 'Admin' || user?.role === 'Trader') && <Button onClick={openCreate}><Plus size={16} /> New deployment</Button>}
      </div>
    </div>
    {error && <Alert variant="danger">{error.message}</Alert>}
    {active.length > 0 && <div className={styles.stopBar}><div><ShieldOff size={18} /><strong>Trading is active</strong><span>Emergency stop blocks new orders and cancels working orders; positions are preserved.</span></div>{user?.role==='Admin' && <Button variant="danger" onClick={() => setStopAll(true)}>Stop all trading</Button>}</div>}

    <div className={styles.stats}>
      <Metric label="Connected accounts" value={overview.data?.connectedAccounts ?? 0} />
      <Metric label="Active deployments" value={overview.data?.activeDeployments ?? 0} />
      <Metric label="Open positions" value={overview.data?.openPositions ?? 0} />
      <Metric label="Needs attention" value={overview.data?.attentionRequired ?? 0} detail={overview.data?.attentionRequired ? 'Review faults or exposure' : 'No current alerts'} />
    </div>

    <section className={styles.section}>
      <div className={styles.sectionTitle}>
        <div><h2>Market watch</h2><p>Current broker-neutral prices across connected accounts.</p></div>
        <div className={styles.headingActions}>
          <Badge variant={streamingAccounts.length > 0 ? 'success' : 'warning'}>{streamingAccounts.length} streaming</Badge>
          <ButtonLink variant="outline-info" to={routes.tradingConnections}><Cable size={16} /> Manage connections</ButtonLink>
          <ButtonLink variant="outline-info" to="/trading/manual">Manual trading</ButtonLink>
        </div>
      </div>
      <div className={styles.marketFilter}>
        <Select label="Account" value={activeMarketAccount} onChange={(event) => setMarketAccount(event.target.value)} options={[
          { value: '', label: 'All accounts' },
          ...streamingAccounts.map((item) => ({ value: item.id, label: item.name })),
        ]} />
      </div>
      <div className={styles.tableWrap}><Table headers={['Account', 'Instrument', 'Bid', 'Ask', 'Last', 'Spread', 'Updated']}>
        {marketRows.map(({ gateway, snapshot, tickSize }) => {
          const stale = Date.now() - Date.parse(snapshot.receivedUtc) > 5_000;
          return <tr key={`${snapshot.gatewayId}:${snapshot.quote.symbol}`}>
            <td><strong>{gateway.name}</strong><small className={styles.block}>{gateway.brokerName ?? 'Awaiting broker'}{gateway.accountId ? ` · ${gateway.accountId}` : ''}</small></td>
            <td className={styles.instrument}>{snapshot.quote.symbol}</td>
            <td className={styles.price}>{formatPrice(snapshot.quote.bid, tickSize)}</td>
            <td className={styles.price}>{formatPrice(snapshot.quote.ask, tickSize)}</td>
            <td className={styles.price}>{snapshot.quote.last == null ? '—' : formatPrice(snapshot.quote.last, tickSize)}</td>
            <td className={styles.price}>{formatPrice(snapshot.quote.ask - snapshot.quote.bid, tickSize)}</td>
            <td>{stale ? <Badge variant="warning">Stale</Badge> : formatTime(snapshot.quote.timeUtc)}</td>
          </tr>;
        })}
        {!marketQuotes.isLoading && marketRows.length === 0 && <tr><td colSpan={7} className={styles.empty}>{streamingAccounts.length === 0
          ? 'No live-price feeds are connected.'
          : activeMarketAccount ? 'Waiting for quotes from this account…' : 'Waiting for the first quote…'}</td></tr>}
      </Table></div>
    </section>

    <section className={styles.section}>
      <div className={styles.sectionTitle}><div><h2>Deployments</h2><p>Monetary values stay in each broker account currency.</p></div></div>
      <div className={styles.filters}>
        <Select label="Environment" value={environment} onChange={(e) => setEnvironment(e.target.value)} options={[{value:'',label:'All environments'},{value:'Paper',label:'Paper'},{value:'Demo',label:'Demo'},{value:'Real',label:'Live · real money'}]} />
        <Select label="Status" value={status} onChange={(e) => setStatus(e.target.value)} options={[{value:'attention',label:'Active or attention required'},{value:'',label:'All statuses'},{value:'Active',label:'Active'},{value:'RiskPaused',label:'Risk paused'},{value:'Stopped',label:'Stopped'}]} />
        <Select label="Account" value={account} onChange={(e) => setAccount(e.target.value)} options={[{value:'',label:'All accounts'},...(overview.data?.accounts ?? []).map((g)=>({value:g.id,label:g.name}))]} />
        <Select label="Strategy" value={strategy} onChange={(e) => setStrategy(e.target.value)} options={[{value:'',label:'All strategies'},...uniqueStrategies(overview.data?.deployments ?? [])]} />
      </div>
      <div className={styles.tableWrap}><Table headers={['Deployment', 'Environment', 'Strategy / account', 'Symbols', 'Status', 'P&L / exposure', 'Last update']}>
        {rows.map((item) => <tr key={item.id}>
          <td><Link className={styles.linkButton} to={routes.deployment(item.id)}>{item.name}</Link></td>
          <td><EnvironmentBadge environment={environmentOf(item)} /></td>
          <td><strong>{item.strategyName}</strong><small className={styles.block}>{item.gatewayName}{item.accountId ? ` · ${item.accountId}` : ''}</small></td>
          <td>{item.symbols.join(', ')}</td>
          <td><LifecycleBadge status={item.status} />{item.error && <div className={styles.error}><AlertTriangle size={12} />{item.error}</div>}</td>
          <td><span>{formatMoney(item.realizedPnl)} realized</span><small className={styles.block}>{formatMoney(item.unrealizedPnl)} unrealized · {item.positionCount} positions / {item.workingOrderCount} orders</small></td>
          <td>{formatTime(item.updatedUtc)}</td>
        </tr>)}
        {!overview.isLoading && rows.length === 0 && <tr><td colSpan={7} className={styles.empty}>No deployments match these filters.</td></tr>}
      </Table></div>
    </section>

    {searchParams.get('create') === '1' && <DeploymentWizard
      accounts={overview.data?.accounts ?? []}
      backtestId={searchParams.get('backtestId') ?? undefined}
      onClose={closeCreate}
      onCreated={(deploymentId) => navigate(routes.deployment(deploymentId))}
    />}
    {stopAll && <ConfirmDialog title="Stop all trading?" confirmLabel="Latch emergency stop" confirmVariant="danger" onCancel={()=>setStopAll(false)} onConfirm={async()=>{await tradingApi.emergencyStopAll();setStopAll(false);await overview.refetch();}}><p>New orders will be blocked and application-owned working orders cancelled on every gateway. Open positions will remain unchanged.</p></ConfirmDialog>}
  </div>;
}

function environmentOf(item: TradingDeploymentDto): TradingEnvironment | null { return item.accountEnvironment; }
export function buildMarketRows(accounts: TradingGatewayDto[], snapshots: LiveQuoteSnapshot[], accountId = '') {
  const accountsById = new Map(accounts.map((item) => [item.id, item]));
  return snapshots.flatMap((snapshot) => {
    const gateway = accountsById.get(snapshot.gatewayId);
    if (!gateway || (accountId && gateway.id !== accountId)) return [];
    const tickSize = gateway.symbols.find((item) => item.symbol === snapshot.quote.symbol)?.tickSize;
    return [{ gateway, snapshot, tickSize }];
  }).sort((left, right) => left.gateway.name.localeCompare(right.gateway.name) ||
    left.snapshot.quote.symbol.localeCompare(right.snapshot.quote.symbol));
}
function uniqueStrategies(items: TradingDeploymentDto[]) {
  return [...new Map(items.map(item=>[item.strategySnapshotHash,{value:item.strategySnapshotHash,label:item.strategyName}])).values()];
}
